import { computed, ref } from 'vue';
import CommissionAgreementsAPI from 'dashboard/api/commissionAgreements';
import CommissionLedgerAPI from 'dashboard/api/commissionLedger';

const responseBody = response =>
  response?.data?.payload ?? response?.data ?? {};

const collectionFrom = response => {
  const body = responseBody(response);
  return Array.isArray(body) ? body : body?.data || [];
};

const metaFrom = response => responseBody(response)?.meta || {};

const errorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors.join(' ');
  }

  return data.message || data.error || fallback;
};

const isUnavailable = error => [403, 404].includes(error?.response?.status);

export function useCommissionAgreements() {
  const agreements = ref([]);
  const ledger = ref([]);
  const agreementMeta = ref({});
  const ledgerMeta = ref({});
  const selectedAgreement = ref(null);
  const filters = ref({});
  const loading = ref(false);
  const detailLoading = ref(false);
  const saving = ref(false);
  const error = ref('');
  const actionError = ref('');
  const unavailable = ref(false);
  const unavailableMessage = ref('');

  const captureUnavailable = requestError => {
    if (!isUnavailable(requestError)) return;

    unavailable.value = true;
    unavailableMessage.value = errorMessage(
      requestError,
      'Acordos de comissão indisponíveis para esta conta.'
    );
  };

  const load = async (nextFilters = filters.value) => {
    filters.value = { ...nextFilters };
    loading.value = true;
    error.value = '';
    unavailable.value = false;
    unavailableMessage.value = '';

    try {
      const [agreementsResponse, ledgerResponse] = await Promise.all([
        CommissionAgreementsAPI.list(filters.value),
        CommissionLedgerAPI.list(filters.value),
      ]);
      agreements.value = collectionFrom(agreementsResponse);
      ledger.value = collectionFrom(ledgerResponse);
      agreementMeta.value = metaFrom(agreementsResponse);
      ledgerMeta.value = metaFrom(ledgerResponse);
      return true;
    } catch (loadError) {
      error.value = errorMessage(
        loadError,
        'Não foi possível carregar os acordos de comissão.'
      );
      captureUnavailable(loadError);
      return false;
    } finally {
      loading.value = false;
    }
  };

  const open = async id => {
    if (!id) return null;

    detailLoading.value = true;
    actionError.value = '';
    try {
      const response = await CommissionAgreementsAPI.show(id);
      selectedAgreement.value = responseBody(response);
      return selectedAgreement.value;
    } catch (openError) {
      actionError.value = errorMessage(
        openError,
        'Não foi possível carregar o histórico do acordo.'
      );
      return null;
    } finally {
      detailLoading.value = false;
    }
  };

  const create = async payload => {
    saving.value = true;
    actionError.value = '';
    try {
      const response = await CommissionAgreementsAPI.create(payload);
      selectedAgreement.value = responseBody(response);
      await load();
      return selectedAgreement.value;
    } catch (createError) {
      actionError.value = errorMessage(
        createError,
        'Não foi possível criar o acordo de comissão.'
      );
      captureUnavailable(createError);
      return null;
    } finally {
      saving.value = false;
    }
  };

  const publish = async id => {
    if (!id) return null;

    saving.value = true;
    actionError.value = '';
    try {
      const response = await CommissionAgreementsAPI.publish(id);
      selectedAgreement.value = responseBody(response);
      await load();
      return selectedAgreement.value;
    } catch (publishError) {
      actionError.value = errorMessage(
        publishError,
        'Não foi possível publicar o acordo de comissão.'
      );
      captureUnavailable(publishError);
      return null;
    } finally {
      saving.value = false;
    }
  };

  return {
    agreements,
    agreementMeta,
    actionError,
    detailLoading,
    error,
    filters,
    ledger,
    ledgerMeta,
    loading,
    selectedAgreement,
    saving,
    totalAgreements: computed(() => agreementMeta.value.count || 0),
    totalLedger: computed(() => ledgerMeta.value.count || 0),
    unavailable,
    unavailableMessage,
    create,
    load,
    open,
    publish,
  };
}
